import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';
import apiService from '../services/api';

const Hero = () => {
  const [heroData, setHeroData] = useState(null);

  const heroHighlights = [
    {
      title: 'AI-first product design',
      description: 'From ideation to deployment, we build intelligent products with measurable ROI.'
    },
    {
      title: 'Data-driven insights',
      description: 'We transform complex data into actionable strategy and automation.'
    },
    {
      title: 'Cloud-native scale',
      description: 'Modern architecture built for performance, security, and fast iteration.'
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      const data = await apiService.getHeroData();
      setHeroData(data);
    };
    fetchData();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!heroData) return null;

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${heroData.backgroundImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-indigo-600/10" />
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-400/20 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200 mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">AI-Powered Solutions</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-6xl lg:text-8xl font-light tracking-tight text-gray-900 mb-6 animate-fade-in-up">
            {heroData.title}
          </h1>

          {/* Subtitle */}
          <h2 className="text-3xl lg:text-4xl font-light text-gray-700 mb-6 animate-fade-in-up delay-200">
            {heroData.subtitle}
          </h2>

          {/* Description */}
          <p className="text-lg lg:text-xl text-gray-600 mb-12 max-w-2xl leading-relaxed animate-fade-in-up delay-300">
            {heroData.description}
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4 mb-16 animate-fade-in-up delay-400">
            <Button
              size="lg"
              onClick={() => scrollToSection('contact')}
              className="bg-black text-white hover:bg-gray-800 transition-all duration-200 hover:scale-105 group"
            >
              {heroData.cta.primary}
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => scrollToSection('services')}
              className="border-gray-300 hover:border-black transition-all duration-200 hover:scale-105"
            >
              {heroData.cta.secondary}
            </Button>
          </div>

          {/* Highlights */}
          <div className="grid grid-cols-1 gap-6 pt-10 border-t border-slate-200/60 animate-fade-in-up delay-500 sm:grid-cols-2 xl:grid-cols-3">
            {heroHighlights.map((item, index) => (
              <div
                key={index}
                className="group overflow-hidden rounded-[2rem] border border-white/25 bg-white/15 p-6 shadow-[0_20px_60px_-35px_rgba(15,23,42,0.45)] backdrop-blur-xl transition duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="mb-5 flex items-center justify-between gap-3">
                  {/* <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-sm font-semibold text-white shadow-sm shadow-blue-600/20">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="rounded-full bg-slate-100/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-600">
                    Pillar
                  </span> */}
                </div>

                <h3 className="text-xl text-slate-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-sm leading-7 text-slate-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;